/* eslint-disable no-console */
import React, { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import toast from "react-hot-toast";
import { Loader, Header } from "../../components";
import KebabMenu from "../../components/KebabMenu";
import { generarReciboPDF } from "../../components/ReciboSueldoPDF";
import { useAuthContext } from "../../context/auth";
import {
  getEmpleados,
  crearEmpleado,
  actualizarEmpleado,
  getVacacionesEmpleado,
  getVacacionesPais,
  agregarVacaciones,
  getRemuneracionesEmpleado,
  crearRemuneracion,
  actualizarRemuneracion,
} from "../../utils";
import { calcularLiquidacionChile } from "../../utils";
import { autoMaterializarComisionesRRHH } from "../../utils/autoMaterializarComisiones";
import {
  getUFValue,
  getUTMValue,
  isLoaded,
  isUTMLoaded,
  loadExchangeRates,
  loadUTM,
} from "../../utils/exchangeRateService";
import { fechaPagoEstimada } from "../../utils/fechaPagoSueldo";
import {
  getPagosEmpleados,
  getHaberesPais,
  getFechasPagoPais,
  crearHaberEmpleado,
  eliminarHaberEmpleado,
  sumarPagadoLiquido,
  saldoLiquidoPorPeriodo,
} from "../../utils/pagosEmpleados";
import { remuneracionVigenteParaMes } from "../../utils/remuneracionVigente";
import { useCurrency } from "../../utils/useCurrency";
import styles from "./stylesEmpleados.module.scss";

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

// Estado inicial del formulario de remuneración (incluye previsionales). Reutilizado en
// el useState inicial, al abrir el modal y tras guardar. Cap AFP arranca en 10% (norma).
const REM_FORM_VACIO = {
  inicio_vigencia: "",
  fin_vigencia: "",
  sueldo_base: "",
  comision_porcentaje: "",
  bonos_fijo: "",
  bonos_variable: "",
  afp_nombre: "",
  afp_cap_individual_pct: "10",
  afp_comision_pct: "",
  salud_tipo: "",
  salud_isapre_uf: "",
};

const GestionEmpleados = () => {
  const { pais } = useAuthContext();
  const { formatAmount } = useCurrency();

  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showVacaciones, setShowVacaciones] = useState(false);
  const [showRemuneraciones, setShowRemuneraciones] = useState(false);
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [vacaciones, setVacaciones] = useState([]); // del modal (1 empleado)
  const [vacacionesTodas, setVacacionesTodas] = useState([]); // para cards
  const [pagosEmpleados, setPagosEmpleados] = useState([]);
  // Haberes imponibles por-mes (bono one-off / comisión) — se pasan a calcularLiquidacionChile
  // como haberesMes. Cargados por país; se filtran por empleado/período en haberesDelMes.
  const [haberes, setHaberes] = useState([]);
  // Overrides de fecha de pago (pago_sueldo_fecha) — para la FECHA del bloque PRÓXIMO SUELDO.
  const [fechasPago, setFechasPago] = useState([]);
  const [showEgresados, setShowEgresados] = useState(false);
  const [remuneraciones, setRemuneraciones] = useState([]);
  const [remuneracionEditando, setRemuneracionEditando] = useState(null);
  // Campo de monto (sueldo/bonos) enfocado: mientras se edita se muestran dígitos crudos;
  // al salir del foco se re-formatea con separador de miles (formatMonto).
  const [montoFocus, setMontoFocus] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    fecha_nacimiento: "",
    inicio_contrato: "",
    fin_contrato: "",
    rut: "",
    cargo: "",
    telefono: "",
    email: "",
    devenga_vacaciones: true,
    pais,
  });
  const [vacacionForm, setVacacionForm] = useState({
    fecha_inicio: "",
    fecha_fin: "",
    notas: "",
  });
  const [showEgreso, setShowEgreso] = useState(false);
  const [egresoForm, setEgresoForm] = useState({
    egreso_fecha: "",
    egreso_monto: "",
  });
  const [remuneracionForm, setRemuneracionForm] = useState(REM_FORM_VACIO);
  // ─── Modal de haberes (bono one-off / comisión del mes) ───
  const [showHaberes, setShowHaberes] = useState(false);
  const [haberMontoFocus, setHaberMontoFocus] = useState(false);
  const [haberPeriodo, setHaberPeriodo] = useState({
    mes: new Date().getMonth() + 1,
    ano: new Date().getFullYear(),
  });
  const [haberForm, setHaberForm] = useState({ tipo: "bono", monto: "", concepto: "", imponible: true });
  // ─── Modal de liquidación ─────────────────────────────────
  const [showLiquidacion, setShowLiquidacion] = useState(false);
  const [liquidacionEmpleado, setLiquidacionEmpleado] = useState(null);
  const [liquidacionMes, setLiquidacionMes] = useState(new Date().getMonth() + 1);
  const [liquidacionAno, setLiquidacionAno] = useState(new Date().getFullYear());
  // La columna "Líquido a pagar" usa calcularLiquidacionChile, que necesita la UF del mes
  // (tramo Isapre) y la UTM del mes (tramos del impuesto único). Sin las tasas cargadas
  // getUFValue/getUTMValue devuelven fallback. El flag fuerza el re-render cuando terminan
  // de cargar ambas (loadExchangeRates/loadUTM no tienen state).
  const [ratesListas, setRatesListas] = useState(isLoaded() && isUTMLoaded());

  useEffect(() => {
    if (isLoaded() && isUTMLoaded()) return;
    Promise.all([loadExchangeRates(), loadUTM()])
      .then(() => setRatesListas(true))
      .catch((e) => console.error("Error cargando tasas:", e));
  }, []);

  // Auto-materialización de comisiones cobradas (Prod-1a): una vez por montaje (guard contra el doble
  // efecto de StrictMode y re-renders). Idempotente por (empleado, referencia) → cero doble-pago;
  // solo escribe cobros nuevos post-corte. Fire-and-forget: no bloquea la UI, sin ruido (solo debug).
  const autoMatRef = useRef(null);
  useEffect(() => {
    if (autoMatRef.current === pais) return; // ya corrió para este país (StrictMode/re-render safe)
    autoMatRef.current = pais;
    autoMaterializarComisionesRRHH(pais).catch((e) => console.error("[RRHH] auto-materialización:", e));
  }, [pais]);

  useEffect(() => {
    const fetchEmpleados = async () => {
      try {
        setLoading(true);
        const [empleadosData, pagosData, vacsData, haberesData, fechasPagoData] = await Promise.all([
          getEmpleados(pais),
          getPagosEmpleados({ pais }),
          getVacacionesPais(pais),
          getHaberesPais(pais),
          getFechasPagoPais(pais),
        ]);
        setEmpleados(empleadosData || []);
        setPagosEmpleados(pagosData || []);
        setVacacionesTodas(vacsData || []);
        setHaberes(haberesData || []);
        setFechasPago(fechasPagoData || []);
      } catch (error) {
        console.error("Error al cargar empleados:", error);
        alert("Error al cargar empleados");
      } finally {
        setLoading(false);
      }
    };
    fetchEmpleados();
  }, [pais]);

  useEffect(() => {
    if (empleados.length > 0) {
      const fetchAllRemuneraciones = async () => {
        try {
          const allRem = [];
          for (const emp of empleados) {
            const data = await getRemuneracionesEmpleado(emp.id);
            allRem.push(...(data || []));
          }
          setRemuneraciones(allRem);
        } catch (error) {
          console.error("Error al cargar todas las remuneraciones:", error);
        }
      };
      fetchAllRemuneraciones();
    }
  }, [empleados]);

  // Cálculo de vacaciones — usa vacacionesTodas para no depender del modal.
  // Considera fin_contrato como tope superior. 15 días/año Chile, 30 días/año Perú.
  const calcularVacacionesEmpleado = (empleado) => {
    if (!empleado.inicio_contrato) return { generados: 0, usados: 0, disponibles: 0 };
    // Flag de la ficha (4.1b): si no devenga, 0 días generados (aplica Chile y Perú).
    if (empleado.devenga_vacaciones === false) return { generados: 0, usados: 0, disponibles: 0 };
    const inicio = new Date(empleado.inicio_contrato);
    const hasta = empleado.fin_contrato ? new Date(empleado.fin_contrato) : new Date();
    const diasPorAno = empleado.pais === "Perú" ? 30 : 15;
    const anosTrabajados = (hasta - inicio) / (1000 * 60 * 60 * 24 * 365.25);
    const diasGenerados = Math.floor(anosTrabajados * diasPorAno);
    const diasUsados = (vacacionesTodas || [])
      .filter((v) => v.empleado_id === empleado.id)
      .reduce((total, v) => {
        const ini = new Date(v.fecha_inicio);
        const fin = new Date(v.fecha_fin);
        return total + Math.ceil((fin - ini) / (1000 * 60 * 60 * 24)) + 1;
      }, 0);
    return {
      generados: diasGenerados,
      usados: diasUsados,
      disponibles: Math.max(0, diasGenerados - diasUsados),
    };
  };

  // Wrapper para mantener compatibilidad con el modal de vacaciones (state singular).
  const calcularVacaciones = (empleado) => calcularVacacionesEmpleado(empleado);

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return "-";
    return new Date(fechaStr + "T12:00:00").toLocaleDateString("es", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getPagosDelEmpleado = (empleadoId) => pagosEmpleados.filter((p) => p.empleado_id === empleadoId);

  // Los tres helpers que siguen ya no se consumen desde la tabla (la card los mostraba).
  // Se conservan a propósito: son la base de la ficha del empleado, que es la próxima fase.
  // eslint-disable-next-line no-unused-vars
  const getTotalPagado2026 = (empleadoId) =>
    pagosEmpleados
      .filter((p) => p.empleado_id === empleadoId && p.periodo_año === 2026)
      .reduce((sum, p) => sum + (Number(p.monto) || 0), 0);

  // eslint-disable-next-line no-unused-vars
  const getUltimoLiquido = (empleadoId) => {
    const sueldos = pagosEmpleados
      .filter((p) => p.empleado_id === empleadoId && p.tipo === "sueldo")
      .sort((a, b) => new Date(b.fecha_pago) - new Date(a.fecha_pago));
    return sueldos[0]?.monto || null;
  };

  // eslint-disable-next-line no-unused-vars
  const getUltimoBono = (empleadoId) => {
    const bonos = getPagosDelEmpleado(empleadoId)
      .filter((p) => p.tipo === "bono")
      .sort((a, b) => new Date(b.fecha_pago) - new Date(a.fecha_pago));
    return bonos[0] || null;
  };

  // eslint-disable-next-line no-unused-vars
  const getUltimaComision = (empleadoId) => {
    const coms = getPagosDelEmpleado(empleadoId)
      .filter((p) => p.tipo === "comision")
      .sort((a, b) => new Date(b.fecha_pago) - new Date(a.fecha_pago));
    return coms[0] || null;
  };

  // Remuneración efectiva del empleado para un mes PUNTUAL (regla 10.1: misma fórmula que la caja,
  // centralizada en el util `remuneracionVigenteParaMes`). Resuelve por la vigencia del mes pedido —
  // NO la de hoy — y devuelve null si ese mes no está cubierto por ninguna vigencia (no se liquida:
  // espejo del `continue` de la proyección de caja). Reemplaza al viejo getRemuneracionVigente, que
  // resolvía siempre contra hoy y liquidaba cualquier mes con la vigencia actual.
  const remVigenteDe = (empleado, año, mes) =>
    remuneracionVigenteParaMes(
      (remuneraciones || []).filter((r) => r.empleado_id === empleado.id),
      año,
      mes
    );

  const monedaPais = pais === "Chile" ? "CLP" : "PEN";
  const formatMonto = (monto) => formatAmount(monto || 0, monedaPais);

  // Mes/año en curso — misma referencia para las tasas (UF/UTM del último día del mes, la
  // convención de los recibos del contador) y para sumar los pagos/adelantos del período.
  const hoyUF = new Date();
  const mesActual = hoyUF.getMonth() + 1;
  const anoActual = hoyUF.getFullYear();
  // Último día del mes en curso: día 0 del mes SIGUIENTE (getMonth()+1). La UF varía a diario y
  // el recibo usa la del cierre de mes (ej. jun → 30-jun, no 28-jun). getUFValue no se toca.
  const fechaTasasActual = new Date(anoActual, hoyUF.getMonth() + 1, 0).toISOString().split("T")[0];
  const ufActual = ratesListas ? getUFValue(fechaTasasActual) : 0;
  const utmActual = ratesListas ? getUTMValue(fechaTasasActual) : 0;

  // Adelantos ya pagados del empleado en el mes/año en curso (state pagosEmpleados, sin queries).
  const adelantosDelMes = (empleadoId) =>
    pagosEmpleados
      .filter(
        (p) =>
          p.empleado_id === empleadoId &&
          p.tipo === "adelanto" &&
          p.periodo_año === anoActual &&
          p.periodo_mes === mesActual
      )
      .reduce((s, p) => s + Number(p.monto || 0), 0);

  // Haberes imponibles del empleado en un período (state `haberes`, sin queries). Array
  // {monto, imponible} → se pasa como haberesMes a calcularLiquidacionChile.
  const haberesDelMes = (empleadoId, año, mes) =>
    (haberes || []).filter((h) => h.empleado_id === empleadoId && h.periodo_año === año && h.periodo_mes === mes);

  // Comisión materializada (haberes tipo 'comision') de un empleado en un período.
  const comisionDelPeriodo = (empleadoId, año, mes) =>
    (haberes || [])
      .filter(
        (h) => h.empleado_id === empleadoId && h.periodo_año === año && h.periodo_mes === mes && h.tipo === "comision"
      )
      .reduce((s, h) => s + Number(h.monto || 0), 0);

  // Adelantos ya registrados de un empleado en un período (generaliza adelantosDelMes).
  const adelantosDelPeriodo = (empleadoId, año, mes) =>
    pagosEmpleados
      .filter(
        (p) => p.empleado_id === empleadoId && p.tipo === "adelanto" && p.periodo_año === año && p.periodo_mes === mes
      )
      .reduce((s, p) => s + Number(p.monto || 0), 0);

  // ¿El sueldo de un período ya fue pagado? (existe pago tipo 'sueldo' del período).
  const sueldoPagadoEnMes = (empleadoId, año, mes) =>
    pagosEmpleados.some(
      (p) => p.empleado_id === empleadoId && p.tipo === "sueldo" && p.periodo_año === año && p.periodo_mes === mes
    );

  // UF/UTM del último día de un mes (misma convención que las tasas del mes en curso).
  const tasasDelMes = (año, mes) => {
    if (!ratesListas) return { uf: 0, utm: 0 };
    const f = new Date(año, mes, 0).toISOString().split("T")[0]; // día 0 del mes siguiente = último día
    return { uf: getUFValue(f), utm: getUTMValue(f) };
  };

  // Próximo período a liquidar: si el sueldo del mes en curso ya está pagado → mes siguiente; si no,
  // el mes en curso (el sueldo del mes todavía por pagar).
  const proximoPeriodo = (empleadoId) => {
    if (!sueldoPagadoEnMes(empleadoId, anoActual, mesActual)) return { año: anoActual, mes: mesActual };
    return mesActual === 12 ? { año: anoActual + 1, mes: 1 } : { año: anoActual, mes: mesActual + 1 };
  };

  // Override manual de fecha de pago (pago_sueldo_fecha) para un empleado/período, o null.
  const overrideFechaPago = (empleadoId, año, mes) =>
    (fechasPago || []).find((f) => f.empleado_id === empleadoId && f.periodo_año === año && f.periodo_mes === mes)
      ?.fecha_pago || null;

  // Fila de la tabla. Total imponible y Líquido salen de calcularLiquidacionChile —
  // la MISMA función que usa el modal de Liquidación — para que la tabla y la
  // liquidación real del empleado no puedan divergir. La card anterior sumaba solo
  // sueldo base + gratificación y se comía los bonos (Franco: 1.695.044 vs 1.866.086).
  const getFilaEmpleado = (emp) => {
    const rem = remVigenteDe(emp, anoActual, mesActual);
    const sueldoBase = Number(rem?.sueldo_base) || Number(emp.sueldo_base) || 0;
    const bonos = (Number(rem?.bonos_fijo) || 0) + (Number(rem?.bonos_variable) || 0);

    const adelantos = adelantosDelMes(emp.id);
    const comisionMes = comisionDelPeriodo(emp.id, anoActual, mesActual);

    // Bloque PRÓXIMO SUELDO — fecha estimada del próximo período (override si hay). Chile y Perú.
    const { año: pAño, mes: pMes } = proximoPeriodo(emp.id);
    const fechaProximo = overrideFechaPago(emp.id, pAño, pMes) || fechaPagoEstimada(pAño, pMes);

    // Perú no pasa por la liquidación chilena (gratificación y descuentos son otros; sin NETO EST).
    if (emp.pais !== "Chile") {
      return {
        emp,
        sueldoBase,
        gratificacion: null,
        bonos,
        totalImponible: sueldoBase + bonos,
        descuentos: null,
        adelantos,
        neto: null,
        comisionMes,
        fechaProximo,
        netoEstProximo: null,
      };
    }

    const liq = calcularLiquidacionChile(
      rem || { sueldo_base: sueldoBase },
      ufActual,
      0,
      utmActual,
      haberesDelMes(emp.id, anoActual, mesActual)
    );
    const liquidoBruto = rem ? liq.liquido : null;

    // NETO del mes en curso: MISMA fuente que Flujo de Caja (saldoLiquidoPorPeriodo en
    // pagosEmpleados.js, regla 10.1) para que Empleados y Caja no diverjan. Imputación ESTRICTA por
    // período: saldo = devengado − pagado del mes (ancla al recibo + allowlist P3.17 intactas; sin
    // max(0), sin arrastre). Reemplaza el viejo `max(0, motor − sumarPagadoLiquido)` mono-mes, que no
    // anclaba (mostraba el "fantasma" de un mes cerrado sobre-estimado) ni compartía código con Caja.
    let netoMes = null;
    if (liquidoBruto !== null) {
      const { subledger } = saldoLiquidoPorPeriodo({
        devengados: [{ año: anoActual, mes: mesActual, devengadoMotor: liq.liquido }],
        pagos: pagosEmpleados,
        empleadoId: emp.id,
      });
      netoMes = subledger[0].saldo;
    }

    // NETO EST. del próximo período: líquido del motor (incluye la comisión ya materializada de ese
    // período vía haberesDelMes) menos los adelantos ya registrados de ese período. max(0, …).
    const { uf: ufP, utm: utmP } = tasasDelMes(pAño, pMes);
    const liqProximo = calcularLiquidacionChile(
      rem || { sueldo_base: sueldoBase },
      ufP,
      0,
      utmP,
      haberesDelMes(emp.id, pAño, pMes)
    );
    const netoEstProximo = rem ? Math.max(0, liqProximo.liquido - adelantosDelPeriodo(emp.id, pAño, pMes)) : null;

    return {
      emp,
      sueldoBase: liq.sueldoBase,
      gratificacion: liq.gratificacion,
      bonos: liq.bonosFijo + liq.bonosVariable,
      totalImponible: liq.totalImponible,
      // Descuentos legales (AFP + salud + cesantía + impuesto único), tal como los suma
      // calcularLiquidacionChile — el desglose completo vive en el modal Liquidación.
      // Sin remuneración vigente no hay datos previsionales: el modal también se niega a
      // calcular en ese caso. Mostramos "—" en vez de un número inventado.
      descuentos: rem ? liq.totalDescuentosLegales : null,
      adelantos,
      comisionMes,
      fechaProximo,
      netoEstProximo,
      neto: netoMes,
    };
  };

  // Último período liquidado (basado en pagos_empleados, tipo 'sueldo').
  // Formato "MM - YYYY" (ej. "06 - 2026"); el encabezado ya dice "liquidado".
  const getMesLiquidado = (empleadoId) => {
    const sueldos = pagosEmpleados
      .filter((p) => p.empleado_id === empleadoId && p.tipo === "sueldo")
      .sort((a, b) => {
        if (b.periodo_año !== a.periodo_año) return b.periodo_año - a.periodo_año;
        return b.periodo_mes - a.periodo_mes;
      });
    if (!sueldos[0]) return null;
    return `${String(sueldos[0].periodo_mes).padStart(2, "0")} - ${sueldos[0].periodo_año}`;
  };

  // Último período con pago tipo 'sueldo' como { año, mes } numérico (para el recibo PDF).
  const getUltimoPeriodoLiquidado = (empleadoId) => {
    const sueldos = pagosEmpleados
      .filter((p) => p.empleado_id === empleadoId && p.tipo === "sueldo")
      .sort((a, b) =>
        b.periodo_año !== a.periodo_año ? b.periodo_año - a.periodo_año : b.periodo_mes - a.periodo_mes
      );
    return sueldos[0] ? { año: sueldos[0].periodo_año, mes: sueldos[0].periodo_mes } : null;
  };

  // Descarga el recibo del último período liquidado. Usa EL MISMO motor/inputs que el modal
  // Liquidación (calcularLiquidacionChile con rem vigente + UF/UTM del último día del mes +
  // haberes del período) → coincide al peso. Solo Chile (el motor es chileno).
  const handleDescargarRecibo = async (emp) => {
    const periodo = getUltimoPeriodoLiquidado(emp.id);
    if (!periodo || emp.pais !== "Chile") return;
    const rem = remVigenteDe(emp, periodo.año, periodo.mes);
    if (!rem) {
      alert("Sin remuneración vigente para ese mes: no se puede generar el recibo.");
      return;
    }
    const { uf, utm } = tasasDelMes(periodo.año, periodo.mes);
    const liq = calcularLiquidacionChile(rem, uf, 0, utm, haberesDelMes(emp.id, periodo.año, periodo.mes));
    try {
      await generarReciboPDF(emp, { ...liq, rem, uf, utm }, periodo.año, periodo.mes);
    } catch (e) {
      console.error("Error al generar el recibo PDF:", e);
      alert("Error al generar el recibo PDF");
    }
  };

  const handleOpenForm = (empleado = null) => {
    if (empleado) {
      setFormData({
        ...empleado,
        fecha_nacimiento: empleado.fecha_nacimiento || "",
        fin_contrato: empleado.fin_contrato || "",
        rut: empleado.rut || "",
        cargo: empleado.cargo || "",
        telefono: empleado.telefono || "",
        email: empleado.email || "",
        devenga_vacaciones: empleado.devenga_vacaciones ?? true,
      });
      setEmpleadoSeleccionado(empleado);
    } else {
      setFormData({
        nombre: "",
        apellido: "",
        fecha_nacimiento: "",
        inicio_contrato: "",
        fin_contrato: "",
        rut: "",
        cargo: "",
        telefono: "",
        email: "",
        devenga_vacaciones: true,
        pais,
      });
      setEmpleadoSeleccionado(null);
    }
    setShowForm(true);
  };

  const handleSaveEmpleado = async () => {
    try {
      // sueldo_base/comision_porcentaje ya NO se escriben (huérfanos tras 4.1a; el sueldo vive en
      // remuneraciones_empleado). La columna se conserva en DB pero la ficha no la toca.
      const cleanedData = {
        nombre: formData.nombre.trim(),
        apellido: formData.apellido.trim(),
        fecha_nacimiento: formData.fecha_nacimiento || null,
        inicio_contrato: formData.inicio_contrato,
        fin_contrato: formData.fin_contrato || null,
        rut: formData.rut?.trim() || null,
        cargo: formData.cargo?.trim() || null,
        telefono: formData.telefono?.trim() || null,
        email: formData.email?.trim() || null,
        devenga_vacaciones: formData.devenga_vacaciones ?? true,
        pais,
      };

      await (empleadoSeleccionado
        ? actualizarEmpleado(empleadoSeleccionado.id, cleanedData)
        : crearEmpleado(cleanedData));
      setShowForm(false);
      const data = await getEmpleados(pais);
      setEmpleados(data || []);
    } catch (error) {
      console.error("Error al guardar empleado:", error);
      alert("Error al guardar empleado");
    }
  };

  const handleOpenVacaciones = async (empleado) => {
    setEmpleadoSeleccionado(empleado);
    try {
      const data = await getVacacionesEmpleado(empleado.id);
      setVacaciones(data || []);
    } catch {
      setVacaciones([]);
    }
    setShowVacaciones(true);
  };

  const handleAddVacacion = async () => {
    try {
      await agregarVacaciones({
        empleado_id: empleadoSeleccionado.id,
        ...vacacionForm,
      });
      setVacacionForm({ fecha_inicio: "", fecha_fin: "", notas: "" });
      const data = await getVacacionesEmpleado(empleadoSeleccionado.id);
      setVacaciones(data || []);
    } catch {
      alert("Error al agregar vacaciones");
    }
  };

  // Re-carga los haberes del país al state `haberes`. Se llama tras alta/baja para que el líquido
  // de la tabla (getFilaEmpleado) y del modal Liquidación se recalculen en el acto.
  const refreshHaberes = async () => {
    try {
      const data = await getHaberesPais(pais);
      setHaberes(data || []);
    } catch (e) {
      console.error("Error al refrescar haberes:", e);
    }
  };

  const handleOpenHaberes = (empleado) => {
    setEmpleadoSeleccionado(empleado);
    setHaberMontoFocus(false);
    setHaberPeriodo({ mes: new Date().getMonth() + 1, ano: new Date().getFullYear() });
    setHaberForm({ tipo: "bono", monto: "", concepto: "", imponible: true });
    setShowHaberes(true);
  };

  const handleAddHaber = async () => {
    const monto = parseFloat(haberForm.monto) || 0;
    if (!empleadoSeleccionado || monto <= 0) {
      alert("Ingresá un monto mayor a 0.");
      return;
    }
    try {
      await crearHaberEmpleado({
        empleado_id: empleadoSeleccionado.id,
        periodo_año: Number(haberPeriodo.ano),
        periodo_mes: Number(haberPeriodo.mes),
        tipo: haberForm.tipo,
        monto,
        imponible: haberForm.imponible,
        concepto: haberForm.concepto.trim() || null,
        origen: "manual",
      });
      setHaberForm({ tipo: "bono", monto: "", concepto: "", imponible: true });
      await refreshHaberes();
    } catch {
      alert("Error al agregar el haber");
    }
  };

  const handleDeleteHaber = async (id) => {
    if (!window.confirm("¿Eliminar este haber?")) return;
    try {
      await eliminarHaberEmpleado(id);
      await refreshHaberes();
    } catch {
      alert("Error al eliminar el haber");
    }
  };

  // Monto numérico (posible "958925.00") → string de dígitos enteros ("958925") para el form.
  const montoADigitos = (v) => (v === "" || v == null ? "" : String(Math.round(Number(v) || 0)));

  // Arma el estado del form a partir de una fila de remuneración. Las fechas se pasan aparte:
  // al PRECARGAR (Agregar) van en blanco (el usuario define el nuevo inicio); al EDITAR van
  // las fechas reales de la fila.
  const remFormDesde = (rem, { inicio_vigencia = "", fin_vigencia = "" } = {}) => ({
    inicio_vigencia,
    fin_vigencia,
    sueldo_base: montoADigitos(rem.sueldo_base),
    comision_porcentaje: rem.comision_porcentaje ?? "",
    bonos_fijo: montoADigitos(rem.bonos_fijo),
    bonos_variable: montoADigitos(rem.bonos_variable),
    afp_nombre: rem.afp_nombre || "",
    afp_cap_individual_pct: rem.afp_cap_individual_pct ?? "",
    afp_comision_pct: rem.afp_comision_pct ?? "",
    salud_tipo: rem.salud_tipo || "",
    salud_isapre_uf: rem.salud_isapre_uf ?? "",
  });

  const handleOpenRemuneraciones = async (empleado) => {
    setEmpleadoSeleccionado(empleado);
    setRemuneracionEditando(null);
    setMontoFocus(null);
    let data = [];
    try {
      data = (await getRemuneracionesEmpleado(empleado.id)) || [];
      setRemuneraciones(data);
    } catch {
      setRemuneraciones([]);
    }
    // Precarga con la vigencia vigente (fin_vigencia null) como plantilla del alta nueva;
    // inicio_vigencia queda en blanco para que el usuario fije el nuevo inicio.
    const vigente = data.find((r) => !r.fin_vigencia);
    setRemuneracionForm(vigente ? remFormDesde(vigente) : REM_FORM_VACIO);
    setShowRemuneraciones(true);
  };

  // ─── Modal liquidación: handlers ──────────────────────────
  // Vista de SOLO LECTURA: calcula y muestra el desglose del líquido del mes.
  // NO registra pagos ni vincula movimientos bancarios — el hecho de pago del sueldo
  // se registra únicamente en la caja (VincularMovimientoModal).
  const handleOpenLiquidacion = async (emp) => {
    setLiquidacionEmpleado(emp);
    const hoy = new Date();
    setLiquidacionMes(hoy.getMonth() + 1);
    setLiquidacionAno(hoy.getFullYear());
    setShowLiquidacion(true);
    try {
      await Promise.all([loadExchangeRates(), loadUTM()]);
    } catch (e) {
      console.error("Error cargando tasas:", e);
    }
  };

  const handleLiquidacionMesChange = (mes, ano) => {
    setLiquidacionMes(mes);
    setLiquidacionAno(ano);
  };

  const handleOpenEgreso = (empleado) => {
    setEmpleadoSeleccionado(empleado);
    setEgresoForm({
      egreso_fecha: empleado.egreso_fecha || "",
      egreso_monto: empleado.egreso_monto || "",
    });
    setShowEgreso(true);
  };

  const handleSaveEgreso = async () => {
    try {
      await actualizarEmpleado(empleadoSeleccionado.id, {
        egreso_fecha: egresoForm.egreso_fecha || null,
        egreso_monto: parseFloat(egresoForm.egreso_monto) || null,
      });
      setShowEgreso(false);
      const data = await getEmpleados(pais);
      setEmpleados(data || []);
      toast.success("Egreso guardado correctamente");
    } catch (error) {
      console.error("Error al guardar egreso:", error);
      toast.error("Error al guardar egreso");
    }
  };

  const handleEditRemuneracion = (rem) => {
    setMontoFocus(null);
    setRemuneracionEditando(rem);
    setRemuneracionForm(
      remFormDesde(rem, { inicio_vigencia: rem.inicio_vigencia, fin_vigencia: rem.fin_vigencia || "" })
    );
  };

  // Día previo a una fecha ISO ("YYYY-MM-DD"). Mediodía para no cruzar de día por TZ.
  const diaPrevio = (isoDate) => {
    const d = new Date(isoDate + "T12:00:00");
    d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  };

  const handleSaveRemuneracion = async () => {
    if (!remuneracionForm.inicio_vigencia) {
      alert("Inicio de vigencia es obligatorio");
      return;
    }
    try {
      const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));
      const cleanedData = {
        inicio_vigencia: remuneracionForm.inicio_vigencia,
        fin_vigencia: remuneracionForm.fin_vigencia || null,
        sueldo_base: parseFloat(remuneracionForm.sueldo_base) || 0,
        comision_porcentaje: parseFloat(remuneracionForm.comision_porcentaje) || 0,
        bonos_fijo: parseFloat(remuneracionForm.bonos_fijo) || 0,
        bonos_variable: parseFloat(remuneracionForm.bonos_variable) || 0,
        afp_nombre: remuneracionForm.afp_nombre.trim() || null,
        afp_cap_individual_pct: numOrNull(remuneracionForm.afp_cap_individual_pct),
        afp_comision_pct: numOrNull(remuneracionForm.afp_comision_pct),
        salud_tipo: remuneracionForm.salud_tipo || null,
        salud_isapre_uf: remuneracionForm.salud_tipo === "isapre" ? numOrNull(remuneracionForm.salud_isapre_uf) : null,
      };

      let newRemuneraciones;
      if (remuneracionEditando) {
        const result = await actualizarRemuneracion(remuneracionEditando.id, cleanedData);
        newRemuneraciones = remuneraciones.map((r) => (r.id === remuneracionEditando.id ? result[0] : r));
      } else {
        // Auto-cierre: la vigencia abierta anterior (fin_vigencia null e inicio previo al
        // nuevo) se cierra el día antes del nuevo inicio, para no solaparlas a mano.
        const anterior = (remuneraciones || [])
          .filter(
            (r) =>
              r.empleado_id === empleadoSeleccionado.id &&
              !r.fin_vigencia &&
              r.inicio_vigencia < remuneracionForm.inicio_vigencia
          )
          .sort((a, b) => new Date(b.inicio_vigencia) - new Date(a.inicio_vigencia))[0];
        if (anterior) {
          await actualizarRemuneracion(anterior.id, { fin_vigencia: diaPrevio(remuneracionForm.inicio_vigencia) });
        }
        const result = await crearRemuneracion({
          empleado_id: empleadoSeleccionado.id,
          ...cleanedData,
        });
        newRemuneraciones = [...remuneraciones, result[0]];
      }

      // Actualiza el state local del modal
      setRemuneraciones(newRemuneraciones);
      setRemuneracionEditando(null);
      setRemuneracionForm(REM_FORM_VACIO);

      // Recarga empleados para que la tabla principal recalcule subrows
      const data = await getEmpleados(pais);
      setEmpleados(data || []);

      // Recarga remuneraciones del empleado actual para mantener sincronizado el modal
      const updatedRem = await getRemuneracionesEmpleado(empleadoSeleccionado.id);
      setRemuneraciones(updatedRem || []);
    } catch (error) {
      console.error("Error al guardar remuneración:", error);
      alert("Error al guardar remuneración");
    }
  };

  // Input de monto con separador de miles: enfocado muestra dígitos crudos (edición fluida),
  // sin foco muestra el formato de moneda (formatMonto). El state guarda solo dígitos enteros.
  const renderMontoInput = (campo) => {
    const raw = remuneracionForm[campo];
    const display = montoFocus === campo ? raw : raw === "" ? "" : formatMonto(Number(raw));
    return (
      <Form.Control
        type="text"
        inputMode="numeric"
        value={display}
        onFocus={() => setMontoFocus(campo)}
        onBlur={() => setMontoFocus(null)}
        onChange={(e) => setRemuneracionForm((f) => ({ ...f, [campo]: e.target.value.replace(/\D/g, "") }))}
      />
    );
  };

  if (loading) return <Loader />;

  return (
    <div className={styles.container}>
      <Header>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <Form.Check
            type="checkbox"
            id="ver-egresados"
            label="Ver egresados"
            checked={showEgresados}
            onChange={(e) => setShowEgresados(e.target.checked)}
          />
          <Button variant="primary" onClick={() => handleOpenForm()} className={styles.nuevoBtn}>
            Nuevo Empleado
          </Button>
        </div>
      </Header>

      <div className={styles.tablaWrapper}>
        {empleados.length === 0 ? (
          <div className="text-center" style={{ padding: 40, color: "#64748b" }}>
            No hay empleados registrados
          </div>
        ) : (
          (() => {
            // Orden pedido: el que más cuesta arriba → Total imponible descendente.
            const sortFn = (a, b) => b.totalImponible - a.totalImponible;
            const ahora = new Date();
            const activos = [...empleados]
              .filter((e) => !e.fin_contrato || new Date(e.fin_contrato) > ahora)
              .map(getFilaEmpleado)
              .sort(sortFn);
            const egresados = [...empleados]
              .filter((e) => e.fin_contrato && new Date(e.fin_contrato) <= ahora)
              .map(getFilaEmpleado)
              .sort(sortFn);

            // Acciones por fila → menú kebab (⋮) compartido, patrón 10.136 (<KebabMenu>).
            // Activos: Liquidación / Remuneración / Vacaciones / Datos. Egresados: Egreso.
            // Mismas acciones que antes (celdas-icono), ahora agrupadas en el kebab.
            const accionesEmpleado = (emp, esEgresada) =>
              esEgresada
                ? [{ label: "Egreso", onClick: () => handleOpenEgreso(emp) }]
                : [
                    { label: "Datos", onClick: () => handleOpenForm(emp) },
                    { label: "Ver liquidación", onClick: () => handleOpenLiquidacion(emp) },
                    { label: "Remuneración", onClick: () => handleOpenRemuneraciones(emp) },
                    { label: "Haberes", onClick: () => handleOpenHaberes(emp) },
                    { label: "Vacaciones", onClick: () => handleOpenVacaciones(emp) },
                  ];

            const renderFila = (fila, esEgresada) => {
              const emp = fila.emp;
              const badgeLiquidado = getMesLiquidado(emp.id);

              return (
                <tr key={emp.id} className={esEgresada ? styles.filaEgresada : undefined}>
                  <td className="text-start align-middle">
                    {emp.nombre} {emp.apellido}
                  </td>
                  <td className="text-center align-middle">{formatFecha(emp.inicio_contrato)}</td>
                  <td className="text-center align-middle">
                    {badgeLiquidado || "-"}
                    {badgeLiquidado && emp.pais === "Chile" && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="ms-2 py-0 px-1"
                        title="Descargar recibo de sueldo (PDF)"
                        onClick={() => handleDescargarRecibo(emp)}
                      >
                        ⬇ recibo
                      </Button>
                    )}
                  </td>
                  <td className="text-end align-middle">{formatMonto(fila.totalImponible)}</td>
                  <td className="text-end align-middle">
                    {fila.comisionMes > 0 ? formatMonto(fila.comisionMes) : "—"}
                  </td>
                  {/* DESCUENTOS: clickeable → abre el modal Liquidación (desglose completo). */}
                  <td
                    className="text-end align-middle"
                    style={{ cursor: "pointer" }}
                    title="Ver liquidación"
                    onClick={() => handleOpenLiquidacion(emp)}
                  >
                    {fila.descuentos === null ? "-" : formatMonto(fila.descuentos)}
                  </td>
                  <td className="text-end align-middle">{fila.adelantos > 0 ? formatMonto(fila.adelantos) : "-"}</td>
                  <td className="text-end align-middle">{fila.neto === null ? "-" : formatMonto(fila.neto)}</td>
                  {/* PRÓXIMO SUELDO */}
                  <td className="text-center align-middle">{formatFecha(fila.fechaProximo)}</td>
                  <td className="text-end align-middle">
                    {fila.netoEstProximo === null ? "-" : formatMonto(fila.netoEstProximo)}
                  </td>
                  <td className="text-center align-middle">
                    <KebabMenu items={accionesEmpleado(emp, esEgresada)} />
                  </td>
                </tr>
              );
            };

            return (
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-dark text-center align-middle">
                  <tr>
                    <th rowSpan={2} style={{ width: "15%" }}>
                      EMPLEADO
                    </th>
                    <th rowSpan={2} style={{ width: "9%" }}>
                      INICIO DE ACTIVIDAD
                    </th>
                    <th rowSpan={2} style={{ width: "11%" }}>
                      ÚLTIMO SUELDO LIQUIDADO
                    </th>
                    <th colSpan={5}>SUELDO DEL MES</th>
                    <th colSpan={2}>PRÓXIMO SUELDO</th>
                    <th rowSpan={2} style={{ width: "5%" }}>
                      ACCIONES
                    </th>
                  </tr>
                  <tr>
                    <th>BRUTO</th>
                    <th>COMISIÓN</th>
                    <th>DESCUENTOS</th>
                    <th>ADELANTOS</th>
                    <th>NETO</th>
                    <th>FECHA</th>
                    <th>NETO EST.</th>
                  </tr>
                </thead>
                <tbody>
                  {activos.map((fila) => renderFila(fila, false))}
                  {showEgresados && egresados.map((fila) => renderFila(fila, true))}
                </tbody>
              </table>
            );
          })()
        )}
      </div>

      {/* Modal Formulario Empleado */}
      <Modal show={showForm} onHide={() => setShowForm(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{empleadoSeleccionado ? "Editar" : "Nuevo"} Empleado</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Apellido</Form.Label>
                <Form.Control
                  value={formData.apellido}
                  onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Fecha Nacimiento</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.fecha_nacimiento}
                  onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Inicio Contrato</Form.Label>
                <Form.Control
                  type="date"
                  required
                  value={formData.inicio_contrato}
                  onChange={(e) => setFormData({ ...formData, inicio_contrato: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Fin Contrato (opcional)</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.fin_contrato}
                  onChange={(e) => setFormData({ ...formData, fin_contrato: e.target.value || null })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>RUT</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="12.345.678-9"
                  value={formData.rut}
                  onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Cargo</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.cargo}
                  onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Teléfono</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-6 mb-3 d-flex align-items-end">
                <Form.Check
                  type="checkbox"
                  id="devenga-vacaciones"
                  label="Devenga vacaciones"
                  checked={formData.devenga_vacaciones}
                  onChange={(e) => setFormData({ ...formData, devenga_vacaciones: e.target.checked })}
                />
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveEmpleado}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Vacaciones */}
      <Modal show={showVacaciones} onHide={() => setShowVacaciones(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Vacaciones - {empleadoSeleccionado && `${empleadoSeleccionado.apellido} ${empleadoSeleccionado.nombre}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4">
            <h5>Agregar Período</h5>
            <div className="row">
              <Form.Group className="col-md-4">
                <Form.Label>Inicio</Form.Label>
                <Form.Control
                  type="date"
                  value={vacacionForm.fecha_inicio}
                  onChange={(e) => setVacacionForm({ ...vacacionForm, fecha_inicio: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-4">
                <Form.Label>Fin</Form.Label>
                <Form.Control
                  type="date"
                  value={vacacionForm.fecha_fin}
                  onChange={(e) => setVacacionForm({ ...vacacionForm, fecha_fin: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="col-md-3">
                <Form.Label>Notas</Form.Label>
                <Form.Control
                  value={vacacionForm.notas}
                  onChange={(e) => setVacacionForm({ ...vacacionForm, notas: e.target.value })}
                />
              </Form.Group>
              <div className="col-md-1 d-flex align-items-end">
                <Button variant="success" onClick={handleAddVacacion}>
                  +
                </Button>
              </div>
            </div>
          </div>

          <h5>Historial</h5>
          <Table striped>
            <thead>
              <tr>
                <th>Inicio</th>
                <th>Fin</th>
                <th>Días</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              {vacaciones.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center">
                    Sin registros
                  </td>
                </tr>
              ) : (
                vacaciones.map((v) => {
                  const dias =
                    Math.ceil((new Date(v.fecha_fin) - new Date(v.fecha_inicio)) / (1000 * 60 * 60 * 24)) + 1;
                  return (
                    <tr key={v.id}>
                      <td>{formatFecha(v.fecha_inicio)}</td>
                      <td>{formatFecha(v.fecha_fin)}</td>
                      <td>{dias}</td>
                      <td>{v.notas || "-"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>

          {empleadoSeleccionado && (
            <div className="mt-3 text-end">
              <strong>Disponibles: {calcularVacaciones(empleadoSeleccionado).disponibles} días</strong>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVacaciones(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Haberes — bono one-off / comisión del mes (haberes_empleado). Las filas
          origen='manual' se agregan/borran acá; las 'motor_comision' (3.2) son read-only. */}
      <Modal show={showHaberes} onHide={() => setShowHaberes(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Haberes - {empleadoSeleccionado && `${empleadoSeleccionado.apellido} ${empleadoSeleccionado.nombre}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4">
            <h5>Agregar haber</h5>
            <div className="row g-2 align-items-end">
              <Form.Group className="col-md-2">
                <Form.Label>Mes</Form.Label>
                <Form.Select
                  value={haberPeriodo.mes}
                  onChange={(e) => setHaberPeriodo((p) => ({ ...p, mes: Number(e.target.value) }))}
                >
                  {MESES.map((m, i) => (
                    <option key={i} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-2">
                <Form.Label>Año</Form.Label>
                <Form.Select
                  value={haberPeriodo.ano}
                  onChange={(e) => setHaberPeriodo((p) => ({ ...p, ano: Number(e.target.value) }))}
                >
                  {[2024, 2025, 2026, 2027].map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-2">
                <Form.Label>Tipo</Form.Label>
                <Form.Select
                  value={haberForm.tipo}
                  onChange={(e) => setHaberForm((f) => ({ ...f, tipo: e.target.value }))}
                >
                  <option value="bono">Bono</option>
                  <option value="comision">Comisión</option>
                  <option value="otro">Otro</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-2">
                <Form.Label>Monto</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  value={
                    haberMontoFocus
                      ? haberForm.monto
                      : haberForm.monto === ""
                        ? ""
                        : formatMonto(Number(haberForm.monto))
                  }
                  onFocus={() => setHaberMontoFocus(true)}
                  onBlur={() => setHaberMontoFocus(false)}
                  onChange={(e) => setHaberForm((f) => ({ ...f, monto: e.target.value.replace(/\D/g, "") }))}
                />
              </Form.Group>
              <Form.Group className="col-md-3">
                <Form.Label>Concepto</Form.Label>
                <Form.Control
                  value={haberForm.concepto}
                  onChange={(e) => setHaberForm((f) => ({ ...f, concepto: e.target.value }))}
                />
              </Form.Group>
              <div className="col-md-1 d-flex align-items-end">
                <Button variant="success" onClick={handleAddHaber}>
                  +
                </Button>
              </div>
            </div>
            <div className="mt-2">
              <Form.Check
                type="checkbox"
                id="haber-imponible"
                label="Imponible (entra al imponible y a la gratificación)"
                checked={haberForm.imponible}
                onChange={(e) => setHaberForm((f) => ({ ...f, imponible: e.target.checked }))}
              />
            </div>
          </div>

          <h5>Haberes cargados</h5>
          <Table striped>
            <thead>
              <tr>
                <th>Período</th>
                <th>Tipo</th>
                <th>Concepto</th>
                <th className="text-end">Monto</th>
                <th className="text-center">Imponible</th>
                <th>Origen</th>
                <th className="text-center">Acción</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const filas = (haberes || [])
                  .filter((h) => empleadoSeleccionado && h.empleado_id === empleadoSeleccionado.id)
                  .sort((a, b) => b.periodo_año - a.periodo_año || b.periodo_mes - a.periodo_mes);
                if (filas.length === 0) {
                  return (
                    <tr>
                      <td colSpan="7" className="text-center">
                        Sin haberes cargados
                      </td>
                    </tr>
                  );
                }
                return filas.map((h) => (
                  <tr key={h.id}>
                    <td>{`${MESES[h.periodo_mes - 1]} ${h.periodo_año}`}</td>
                    <td className="text-capitalize">{h.tipo}</td>
                    <td>{h.concepto || "-"}</td>
                    <td className="text-end">{formatMonto(Number(h.monto))}</td>
                    <td className="text-center">{h.imponible ? "Sí" : "No"}</td>
                    <td>{h.origen === "motor_comision" ? "Motor" : "Manual"}</td>
                    <td className="text-center">
                      {h.origen === "manual" ? (
                        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteHaber(h.id)}>
                          Eliminar
                        </Button>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHaberes(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Remuneraciones */}
      <Modal show={showRemuneraciones} onHide={() => setShowRemuneraciones(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Remuneraciones - {empleadoSeleccionado && `${empleadoSeleccionado.apellido} ${empleadoSeleccionado.nombre}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4 p-3 border rounded">
            <h5 className="mb-3">{remuneracionEditando ? "Editar" : "Agregar"} Remuneración</h5>
            <Form>
              <div className="row">
                <Form.Group className="col mb-3">
                  <Form.Label>Inicio Vigencia</Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={remuneracionForm.inicio_vigencia}
                    onChange={(e) => setRemuneracionForm({ ...remuneracionForm, inicio_vigencia: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="col mb-3">
                  <Form.Label>Sueldo Base</Form.Label>
                  {renderMontoInput("sueldo_base")}
                </Form.Group>
                <Form.Group className="col mb-3">
                  <Form.Label>Comisión %</Form.Label>
                  <Form.Control
                    type="number"
                    value={remuneracionForm.comision_porcentaje}
                    onChange={(e) => setRemuneracionForm({ ...remuneracionForm, comision_porcentaje: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="col mb-3">
                  <Form.Label>Bonos Fijo</Form.Label>
                  {renderMontoInput("bonos_fijo")}
                </Form.Group>
                <Form.Group className="col mb-3">
                  <Form.Label>Bonos Variable</Form.Label>
                  {renderMontoInput("bonos_variable")}
                </Form.Group>
              </div>

              <hr className="my-2" />
              <div className="text-muted mb-2" style={{ fontSize: "0.8rem", fontWeight: 600 }}>
                PREVISIONAL (Chile)
              </div>
              <div className="row">
                <Form.Group className="col mb-3">
                  <Form.Label>AFP</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Provida, Modelo…"
                    value={remuneracionForm.afp_nombre}
                    onChange={(e) => setRemuneracionForm({ ...remuneracionForm, afp_nombre: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="col mb-3">
                  <Form.Label>AFP cap. individual %</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={remuneracionForm.afp_cap_individual_pct}
                    onChange={(e) =>
                      setRemuneracionForm({ ...remuneracionForm, afp_cap_individual_pct: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group className="col mb-3">
                  <Form.Label>AFP comisión %</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={remuneracionForm.afp_comision_pct}
                    onChange={(e) => setRemuneracionForm({ ...remuneracionForm, afp_comision_pct: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="col mb-3">
                  <Form.Label>Salud</Form.Label>
                  <Form.Select
                    value={remuneracionForm.salud_tipo}
                    onChange={(e) => setRemuneracionForm({ ...remuneracionForm, salud_tipo: e.target.value })}
                  >
                    <option value="">—</option>
                    <option value="fonasa">Fonasa</option>
                    <option value="isapre">Isapre</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="col mb-3">
                  <Form.Label>Isapre UF</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.0001"
                    disabled={remuneracionForm.salud_tipo !== "isapre"}
                    value={remuneracionForm.salud_isapre_uf}
                    onChange={(e) => setRemuneracionForm({ ...remuneracionForm, salud_isapre_uf: e.target.value })}
                  />
                </Form.Group>
              </div>

              <div className="d-flex justify-content-end gap-2">
                {remuneracionEditando && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setRemuneracionEditando(null);
                      setMontoFocus(null);
                      setRemuneracionForm(REM_FORM_VACIO);
                    }}
                  >
                    Cancelar edición
                  </Button>
                )}
                <Button variant="primary" onClick={handleSaveRemuneracion}>
                  {remuneracionEditando ? "Actualizar" : "Agregar"}
                </Button>
              </div>
            </Form>
          </div>

          <h5 className="mb-3">Historial de Remuneraciones</h5>
          <table className="table table-bordered table-hover align-middle">
            <thead className="table-dark text-center align-middle">
              <tr>
                <th>INICIO VIGENCIA</th>
                <th>FIN VIGENCIA</th>
                <th>SUELDO BASE</th>
                <th>COMISIÓN %</th>
                <th>BONOS FIJO</th>
                <th>BONOS VARIABLE</th>
                <th>AFP</th>
                <th>SALUD</th>
                <th>ACCIONES</th>
              </tr>
            </thead>
            <tbody>
              {remuneraciones.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center align-middle">
                    Sin registros de remuneraciones
                  </td>
                </tr>
              ) : (
                remuneraciones.map((rem) => (
                  <tr key={rem.id}>
                    <td className="text-center align-middle">{formatFecha(rem.inicio_vigencia)}</td>
                    <td className="text-center align-middle">
                      {rem.fin_vigencia ? formatFecha(rem.fin_vigencia) : "vigente"}
                    </td>
                    <td className="text-end align-middle">{formatAmount(rem.sueldo_base, monedaPais)}</td>
                    <td className="text-end align-middle">{rem.comision_porcentaje || 0}%</td>
                    <td className="text-end align-middle">{formatAmount(rem.bonos_fijo, monedaPais)}</td>
                    <td className="text-end align-middle">{formatAmount(rem.bonos_variable, monedaPais)}</td>
                    <td className="text-center align-middle">
                      {rem.afp_nombre
                        ? `${rem.afp_nombre} ${rem.afp_cap_individual_pct ?? "-"}%+${rem.afp_comision_pct ?? 0}%`
                        : "-"}
                    </td>
                    <td className="text-center align-middle">
                      {rem.salud_tipo === "isapre"
                        ? `Isapre ${rem.salud_isapre_uf ?? "-"} UF`
                        : rem.salud_tipo === "fonasa"
                          ? "Fonasa"
                          : "-"}
                    </td>
                    <td className="text-center align-middle">
                      <Button size="sm" variant="outline-secondary" onClick={() => handleEditRemuneracion(rem)}>
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemuneraciones(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Modal Egreso */}
      <Modal show={showEgreso} onHide={() => setShowEgreso(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Egreso - {empleadoSeleccionado && `${empleadoSeleccionado.nombre} ${empleadoSeleccionado.apellido}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Fecha de Pago</Form.Label>
              <Form.Control
                type="date"
                value={egresoForm.egreso_fecha}
                onChange={(e) => setEgresoForm({ ...egresoForm, egreso_fecha: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Monto {pais === "Chile" ? "(CLP)" : "(PEN)"}</Form.Label>
              <Form.Control
                type="number"
                value={egresoForm.egreso_monto}
                onChange={(e) => setEgresoForm({ ...egresoForm, egreso_monto: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEgreso(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveEgreso}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Liquidación */}
      <Modal show={showLiquidacion} onHide={() => setShowLiquidacion(false)} size="lg">
        <Modal.Header closeButton style={{ background: "#2c3e50", color: "#fff" }}>
          <Modal.Title style={{ fontSize: "16px" }}>
            Liquidación — {liquidacionEmpleado?.nombre} {liquidacionEmpleado?.apellido}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {liquidacionEmpleado &&
            (() => {
              const rem = remVigenteDe(liquidacionEmpleado, liquidacionAno, liquidacionMes);
              // Selectores de mes/año: SIEMPRE visibles (aunque el mes no tenga vigencia) para poder
              // cambiar de período sin quedar trabado. El desglose se muestra solo si hay vigencia.
              const selectorMesAño = (
                <div style={{ display: "flex", gap: "12px", marginBottom: "16px", alignItems: "flex-end" }}>
                  <div>
                    <label style={{ fontSize: "12px", color: "#64748b", display: "block", marginBottom: "4px" }}>
                      Mes
                    </label>
                    <Form.Select
                      size="sm"
                      value={liquidacionMes}
                      onChange={(e) => handleLiquidacionMesChange(Number(e.target.value), liquidacionAno)}
                      style={{ width: "130px" }}
                    >
                      {MESES.map((m, i) => (
                        <option key={i} value={i + 1}>
                          {m}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div>
                    <label style={{ fontSize: "12px", color: "#64748b", display: "block", marginBottom: "4px" }}>
                      Año
                    </label>
                    <Form.Select
                      size="sm"
                      value={liquidacionAno}
                      onChange={(e) => handleLiquidacionMesChange(liquidacionMes, Number(e.target.value))}
                      style={{ width: "90px" }}
                    >
                      {[2024, 2025, 2026, 2027].map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </div>
              );
              // Mes sin vigencia (ej. anterior a la primera vigencia del empleado) → no se liquida:
              // espejo del `continue` de la caja. Sin desglose, pero con los selectores para navegar.
              if (!rem)
                return (
                  <>
                    {selectorMesAño}
                    <p style={{ color: "#e74c3c", margin: 0 }}>
                      Sin remuneración vigente para {MESES[liquidacionMes - 1]} {liquidacionAno}
                    </p>
                  </>
                );
              // UF/UTM del último día del mes liquidado (convención de los recibos). liquidacionMes es
              // 1-based → new Date(año, liquidacionMes, 0) = día 0 del mes siguiente = último del mes.
              const fechaTasasMes = new Date(liquidacionAno, liquidacionMes, 0).toISOString().split("T")[0];
              const ufDelMes = getUFValue(fechaTasasMes);
              const utmDelMes = getUTMValue(fechaTasasMes);
              // Adelantos NO se cargan acá (vienen de caja) → descuentosVarios = 0. Haberes
              // imponibles del mes/año elegido (bono one-off / comisión) → haberesMes.
              const liq = calcularLiquidacionChile(
                rem,
                ufDelMes,
                0,
                utmDelMes,
                haberesDelMes(liquidacionEmpleado.id, liquidacionAno, liquidacionMes)
              );
              const fmt = (n) =>
                new Intl.NumberFormat("es-CL", {
                  style: "currency",
                  currency: "CLP",
                  maximumFractionDigits: 0,
                }).format(n);
              // Pagos reales del mes/año elegido (del state ya cargado, sin queries).
              const pagosDelMes = pagosEmpleados
                .filter(
                  (p) =>
                    p.empleado_id === liquidacionEmpleado.id &&
                    p.periodo_año === liquidacionAno &&
                    p.periodo_mes === liquidacionMes
                )
                .sort((a, b) => new Date(a.fecha_pago) - new Date(b.fecha_pago));
              // Pagado a cuenta del sueldo = sueldo + adelanto del período (sumarPagadoLiquido),
              // NO todos los tipos: comisión/bono quedan en la lista pero no descuentan el líquido.
              // Así A pagar = líquido − pagadoACuenta cierra exacto (mismo util que la tabla NETO).
              const pagadoACuenta = sumarPagadoLiquido(
                pagosEmpleados,
                liquidacionEmpleado.id,
                liquidacionAno,
                liquidacionMes
              );
              const aPagar = Math.max(0, liq.liquido - pagadoACuenta);
              const filas = [
                ["Sueldo base", liq.sueldoBase, false],
                rem.bonos_fijo > 0 ? ["Bono fijo", liq.bonosFijo, false] : null,
                rem.bonos_variable > 0 ? ["Bono variable", liq.bonosVariable, false] : null,
                ["Gratificación", liq.gratificacion, false],
                ["Total imponible", liq.totalImponible, true],
                [
                  `AFP ${rem.afp_nombre || ""} (cap. ${rem.afp_cap_individual_pct ?? 10}% + com. ${rem.afp_comision_pct || 0}%)`,
                  -liq.totalAfp,
                  false,
                ],
                [
                  rem.salud_tipo === "isapre"
                    ? `Isapre (UF ${rem.salud_isapre_uf} × ${ufDelMes.toFixed(2)}, piso 7%)`
                    : "Fonasa (7%)",
                  -liq.totalSalud,
                  false,
                ],
                ["Seg. cesantía (0,6%)", -liq.segCesantia, false],
                ["Base impuesto único", liq.baseImpuesto, false],
                ["Impuesto único", -liq.impuestoUnico, false],
                ["Total descuentos legales", -liq.totalDescuentosLegales, true],
              ].filter(Boolean);
              return (
                <>
                  {selectorMesAño}

                  <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse", marginBottom: "16px" }}>
                    <tbody>
                      {filas.map(([label, valor, bold], i) => (
                        <tr
                          key={i}
                          style={{
                            borderBottom: "1px solid #f1f5f9",
                            background: bold ? "#f8fafc" : "transparent",
                          }}
                        >
                          <td
                            style={{
                              padding: "7px 8px",
                              color: "#475569",
                              fontWeight: bold ? 500 : 400,
                              textAlign: "left",
                            }}
                          >
                            {label}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              textAlign: "right",
                              fontWeight: bold ? 500 : 400,
                              color: valor < 0 ? "#e74c3c" : "#1e293b",
                            }}
                          >
                            {fmt(Math.abs(valor))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Bloque del sueldo (tono suave): Líquido del mes − Adelantos = A pagar. */}
                  <div
                    style={{
                      background: "#f1f5f9",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      padding: "12px 16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "13px",
                        color: "#64748b",
                        marginBottom: "6px",
                      }}
                    >
                      <span>Líquido del mes</span>
                      <span>{fmt(liq.liquido)}</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "13px",
                        color: "#64748b",
                        marginBottom: "8px",
                      }}
                    >
                      <span>Adelantos</span>
                      <span>{pagadoACuenta > 0 ? `− ${fmt(pagadoACuenta)}` : fmt(0)}</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderTop: "1px solid #cbd5e1",
                        paddingTop: "8px",
                      }}
                    >
                      <span style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>A pagar</span>
                      <span style={{ fontSize: "20px", fontWeight: 600, color: "#1e293b" }}>{fmt(aPagar)}</span>
                    </div>
                  </div>

                  {/* Pagos extraordinarios (transporte, comisión, bono, etc.), separados del sueldo. */}
                  <div style={{ marginTop: "20px", borderTop: "1px solid #e2e8f0", paddingTop: "16px" }}>
                    <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 600, marginBottom: "6px" }}>
                      Pagos del mes
                    </div>
                    {pagosDelMes.length === 0 ? (
                      <div style={{ fontSize: "13px", color: "#94a3b8" }}>Sin pagos registrados este mes</div>
                    ) : (
                      <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse" }}>
                        <tbody>
                          {pagosDelMes.map((p) => (
                            <tr key={p.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                              <td style={{ padding: "6px 8px", color: "#475569" }}>{formatFecha(p.fecha_pago)}</td>
                              <td style={{ padding: "6px 8px", color: "#475569", textTransform: "capitalize" }}>
                                {p.tipo}
                              </td>
                              <td style={{ padding: "6px 8px", textAlign: "right", color: "#1e293b" }}>
                                {fmt(Number(p.monto || 0))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              );
            })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowLiquidacion(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GestionEmpleados;
